import { Form, notification } from 'antd'
import { observer } from 'mobx-react-lite'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { UserModuleKeys } from 'sections/LKSection/modules/UserModule/types'
import { getSnilsError, SNILS_LENGTH } from 'sections/LKSection/modules/UserModule/utils/getSnilsError'
import { personalDataApi } from 'sections/LKSection/shared/api'
import { PersonalData } from 'sections/LKSection/shared/components/PersonalData/PersonalData'
import { PersonalUserDataDTO } from 'sections/LKSection/shared/types/types'
import { Margin } from 'components/ui'
import { Flex, Input, Modal, Text } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { REPLACE_NOT_NUMBERS_REGEXP } from 'shared/constants/regExp'

import { EmployeeKeys } from '../../../data'
import { Employee } from '../../../stores/entity/Employee'

type EditEmployeeSnilsModalProps = {
  employee: Employee
  personalData: PersonalUserDataDTO | null
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  setPersonalData: Dispatch<SetStateAction<PersonalUserDataDTO | null>>
}

const getKey = (key: string) => `${EmployeeKeys.employeePage}.${key}`

export const EditEmployeeSnilsModal = observer(
  ({ employee, open, personalData, setOpen, setPersonalData }: EditEmployeeSnilsModalProps) => {
    const { viewEmployeeUiStore: uiStore, viewPvzUiStore } = useStore()

    const [snils, setSnils] = useState(String(personalData?.snils ?? '') || undefined)
    const { t } = useTranslation()

    const pvz = viewPvzUiStore.selectedPvz
    const disabled = !snils || Boolean(personalData?.snils)

    useEffect(() => {
      if (personalData?.snils) {
        setSnils(String(personalData.snils))
      }
    }, [personalData?.snils])

    const saveButtonHandler = async () => {
      if (snils && employee.id) {
        const snilsError = getSnilsError(snils)

        if (snilsError) {
          notification.error({ message: snilsError })
        } else {
          const res = await uiStore.changeUserSnils(snils, employee.id)

          if (res) {
            setOpen(false)
          }
        }
      }
    }

    return (
      <Modal
        title={t(getKey('Редактирование данных сотрудника'))}
        open={open}
        onOk={saveButtonHandler}
        okText={t(getSharedKey('save'))}
        onCancel={() => setOpen(false)}
        okButtonProps={{ disabled }}
      >
        <Flex vertical>
          <Text>{pvz?.address}</Text>

          <PersonalData<PersonalUserDataDTO>
            id={employee.id ?? 0}
            getPersonalData={personalDataApi.getUserData}
            setPersonalData={setPersonalData}
            data={personalData}
          >
            <Text type={employee.isDeleted ? 'secondary' : undefined}>
              {`${personalData?.firstName} ${personalData?.lastName}`}
            </Text>
          </PersonalData>

          <Margin mt={16}>
            <Form layout="vertical">
              <Form.Item label={t(`${UserModuleKeys.profilePage}.СНИЛС`)}>
                <Input
                  placeholder={t(getKey('Введите СНИЛС'))}
                  value={snils}
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(REPLACE_NOT_NUMBERS_REGEXP, '')
                    setSnils(onlyNumbers)
                  }}
                  maxLength={SNILS_LENGTH}
                  disabled={Boolean(personalData?.snils || !personalData)}
                />
              </Form.Item>
            </Form>
          </Margin>
        </Flex>
      </Modal>
    )
  },
)
